"use client";

import type { CSSProperties, ReactNode } from "react";
import * as React from "react";

import type {
	ElementStyles,
	PanelSwitcherOrientation,
	PanelSwitcherTabAlign,
	PanelSwitcherTabVariant,
} from "../ui/types";
import { flatElementStylesToReactCss } from "./flatElementStyles";

/**
 * Shared tab-strip markup for builder canvas and region runtime.
 * Keep both surfaces identical so templates / typography match — same rule as
 * `FaqAccordion`. Writing separate canvas and runtime markup is how WYSIWYG dies.
 */

export type PanelSwitcherPanel = {
	/** Stable id — used for the panel/tab `aria-controls` pair and as the React key. */
	id: string;
	label: string;
	content: ReactNode;
};

export type PanelSwitcherProps = {
	className?: string;
	/** Extra DOM attrs (id, data-pb-node, …) merged onto the root. */
	domProps?: Record<string, unknown>;
	emptyFallback?: ReactNode;
	id: string;
	orientation?: PanelSwitcherOrientation;
	/** CSS length between the tab strip and the panel body. */
	panelGap?: string;
	panels: PanelSwitcherPanel[];
	tabAlign?: PanelSwitcherTabAlign;
	/** CSS length between tabs; the `joined` variant ignores it. */
	tabGap?: string;
	tabVariant?: PanelSwitcherTabVariant;
	/** Author overrides for every tab button; theme tokens remain the fallback. */
	tabStyles?: ElementStyles;
	/** Merged over `tabStyles` on the active tab only. */
	activeTabStyles?: ElementStyles;
	/** Canvas only: keeps the editor's selected panel visible instead of local state. */
	activeIndex?: number;
	onActiveIndexChange?: (index: number) => void;
	style?: CSSProperties;
};

/** Single source for the active-tab look — used by the scoped CSS and the inline fallback. */
const ACTIVE_TAB_BACKGROUND = "var(--tt-color-primary, #0ea5e9)";
const ACTIVE_TAB_COLOR = "#fff";
const TAB_RADIUS = "8px";
/** Author overrides ride in as custom properties so the scoped CSS stays per-node-only. */
const TAB_GAP_VAR = "--pb-panels-tab-gap";
const PANEL_GAP_VAR = "--pb-panels-gap";

export function buildPanelSwitcherCss(nodeId: string): string {
	const scope = `.pb-panels-${nodeId}`;
	return [
		`${scope}{display:flex;gap:var(${PANEL_GAP_VAR},1rem);}`,
		`${scope}[data-orientation="horizontal"]{flex-direction:column;}`,
		`${scope}[data-orientation="vertical"]{flex-direction:row;}`,
		`${scope} .pb-panels-tablist{display:flex;gap:var(${TAB_GAP_VAR},0.4rem);flex-wrap:wrap;}`,
		`${scope}[data-orientation="vertical"] .pb-panels-tablist{flex-direction:column;flex-wrap:nowrap;flex-shrink:0;min-width:12rem;}`,
		`${scope} .pb-panels-tab{padding:0.5rem 0.9rem;border-radius:${TAB_RADIUS};border:1px solid color-mix(in srgb,var(--tt-color-fg,#0f172a) 16%,transparent);background:transparent;color:inherit;font-family:var(--tt-font-sans,system-ui,sans-serif);font-size:0.95em;cursor:pointer;text-align:left;transition:background-color 0.2s ease,border-color 0.2s ease,color 0.2s ease;}`,
		`${scope} .pb-panels-tab:hover{background-color:color-mix(in srgb,var(--tt-color-fg,#0f172a) 6%,transparent);}`,
		`${scope} .pb-panels-tab:focus-visible{outline:2px solid color-mix(in srgb,var(--tt-color-primary,#0ea5e9) 70%,transparent);outline-offset:2px;}`,
		`${scope} .pb-panels-tab[aria-selected="true"]{background-color:${ACTIVE_TAB_BACKGROUND};border-color:${ACTIVE_TAB_BACKGROUND};color:${ACTIVE_TAB_COLOR};}`,
		`${scope} .pb-panels-body{flex:1;min-width:0;}`,

		// Alignment. Horizontal tabs align along the row, vertical ones across the column —
		// same author choice, different axis, so each value needs both spellings.
		`${scope}[data-orientation="horizontal"][data-align="center"] .pb-panels-tablist{justify-content:center;}`,
		`${scope}[data-orientation="horizontal"][data-align="end"] .pb-panels-tablist{justify-content:flex-end;}`,
		`${scope}[data-orientation="vertical"][data-align="center"] .pb-panels-tablist{align-items:center;}`,
		`${scope}[data-orientation="vertical"][data-align="end"] .pb-panels-tablist{align-items:flex-end;}`,
		// Vertical tabs already fill their column (default `align-items:stretch`), and
		// `flex:1` there would stretch them down the page instead — horizontal only.
		`${scope}[data-orientation="horizontal"][data-align="stretch"] .pb-panels-tablist{flex-wrap:nowrap;}`,
		`${scope}[data-orientation="horizontal"][data-align="stretch"] .pb-panels-tab{flex:1 1 0;text-align:center;}`,

		// Joined — one segmented control. The gap is forced to 0 (an author gap would leave
		// the shared borders floating apart) and the -1px pulls the touching borders onto
		// each other so the seam reads as a single hairline.
		`${scope}[data-variant="joined"] .pb-panels-tablist{gap:0;flex-wrap:nowrap;}`,
		`${scope}[data-variant="joined"] .pb-panels-tab{border-radius:0;}`,
		`${scope}[data-variant="joined"] .pb-panels-tab[aria-selected="true"]{position:relative;z-index:1;}`,
		`${scope}[data-orientation="horizontal"][data-variant="joined"] .pb-panels-tab+.pb-panels-tab{margin-left:-1px;}`,
		`${scope}[data-orientation="horizontal"][data-variant="joined"] .pb-panels-tab:first-child{border-radius:${TAB_RADIUS} 0 0 ${TAB_RADIUS};}`,
		`${scope}[data-orientation="horizontal"][data-variant="joined"] .pb-panels-tab:last-child{border-radius:0 ${TAB_RADIUS} ${TAB_RADIUS} 0;}`,
		`${scope}[data-orientation="vertical"][data-variant="joined"] .pb-panels-tab+.pb-panels-tab{margin-top:-1px;}`,
		`${scope}[data-orientation="vertical"][data-variant="joined"] .pb-panels-tab:first-child{border-radius:${TAB_RADIUS} ${TAB_RADIUS} 0 0;}`,
		`${scope}[data-orientation="vertical"][data-variant="joined"] .pb-panels-tab:last-child{border-radius:0 0 ${TAB_RADIUS} ${TAB_RADIUS};}`,

		// Underline — no chrome, just a rule on the leading edge of the active tab.
		`${scope}[data-variant="underline"] .pb-panels-tab{border:0;border-radius:0;}`,
		`${scope}[data-orientation="horizontal"][data-variant="underline"] .pb-panels-tab{border-bottom:2px solid transparent;}`,
		`${scope}[data-orientation="horizontal"][data-variant="underline"] .pb-panels-tab[aria-selected="true"]{border-bottom-color:${ACTIVE_TAB_BACKGROUND};}`,
		`${scope}[data-orientation="vertical"][data-variant="underline"] .pb-panels-tab{border-left:2px solid transparent;}`,
		`${scope}[data-orientation="vertical"][data-variant="underline"] .pb-panels-tab[aria-selected="true"]{border-left-color:${ACTIVE_TAB_BACKGROUND};}`,
		`${scope}[data-variant="underline"] .pb-panels-tab[aria-selected="true"]{background-color:transparent;color:${ACTIVE_TAB_BACKGROUND};}`,
	].join("");
}

export function PanelSwitcher({
	id,
	panels,
	orientation = "horizontal",
	className,
	style,
	domProps,
	emptyFallback,
	activeIndex,
	onActiveIndexChange,
	panelGap,
	tabAlign = "start",
	tabGap,
	tabStyles,
	tabVariant = "separated",
	activeTabStyles,
}: PanelSwitcherProps): React.ReactElement {
	// Inline so author overrides beat the scoped CSS without a specificity war.
	const tabCss = flatElementStylesToReactCss(
		tabStyles as Record<string, unknown> | undefined
	);
	// Re-assert the active-state defaults between the two packs. Inline styles beat the
	// scoped CSS, so without this a base `tabStyles` background would land on the active
	// tab too and silently erase the highlight — every tab looking identical.
	// Underline has no fill and paints its rule from the stylesheet, so it must not
	// re-assert `borderColor` here — an inline value would erase the underline.
	const activeTabDefaults: CSSProperties =
		tabVariant === "underline"
			? { backgroundColor: "transparent", color: ACTIVE_TAB_BACKGROUND }
			: {
					backgroundColor: ACTIVE_TAB_BACKGROUND,
					borderColor: ACTIVE_TAB_BACKGROUND,
					color: ACTIVE_TAB_COLOR,
				};
	const activeTabCss = {
		...tabCss,
		...activeTabDefaults,
		...flatElementStylesToReactCss(
			activeTabStyles as Record<string, unknown> | undefined
		),
	};
	const [internalIndex, setInternalIndex] = React.useState(0);
	// Controlled on the canvas (editor drives selection), uncontrolled on the public site.
	const isControlled = typeof activeIndex === "number";
	const rawIndex = isControlled ? activeIndex : internalIndex;
	// Deleting the active panel would otherwise leave nothing rendered.
	const current = Math.min(
		Math.max(rawIndex, 0),
		Math.max(panels.length - 1, 0)
	);

	const select = (next: number) => {
		if (!isControlled) {
			setInternalIndex(next);
		}
		onActiveIndexChange?.(next);
	};

	const { className: domClass, ...domRest } = (domProps ?? {}) as {
		className?: string;
		[key: string]: unknown;
	};
	const rootClass = [`pb-panels-${id}`, className, domClass]
		.filter(Boolean)
		.join(" ");
	// Custom properties rather than extra CSS rules — keeps `buildPanelSwitcherCss` a
	// pure function of the node id, so the same stylesheet serves every configuration.
	const rootStyle = {
		...style,
		...(tabGap ? { [TAB_GAP_VAR]: tabGap } : {}),
		...(panelGap ? { [PANEL_GAP_VAR]: panelGap } : {}),
	} as CSSProperties;

	return (
		<>
			<style
				dangerouslySetInnerHTML={{ __html: buildPanelSwitcherCss(id) }}
				key={`${id}-pb-panels-css`}
			/>
			<div
				{...domRest}
				className={rootClass}
				data-align={tabAlign}
				data-orientation={orientation}
				data-pb-node={id}
				data-variant={tabVariant}
				style={rootStyle}
			>
				{panels.length === 0 ? (
					(emptyFallback ?? null)
				) : (
					<>
						<div
							aria-orientation={orientation}
							className="pb-panels-tablist"
							role="tablist"
						>
							{panels.map((panel, i) => (
								<button
									aria-controls={`${id}-panel-${panel.id}`}
									aria-selected={i === current}
									className="pb-panels-tab"
									id={`${id}-tab-${panel.id}`}
									key={panel.id}
									onClick={() => select(i)}
									role="tab"
									style={i === current ? activeTabCss : tabCss}
									type="button"
								>
									{panel.label || `Tab ${i + 1}`}
								</button>
							))}
						</div>
						<div className="pb-panels-body">
							{panels.map((panel, i) => (
								<div
									aria-labelledby={`${id}-tab-${panel.id}`}
									// Kept mounted and hidden so canvas edits to an inactive panel
									// are not lost, and so nested content stays in the DOM for SEO.
									hidden={i !== current}
									id={`${id}-panel-${panel.id}`}
									key={panel.id}
									role="tabpanel"
								>
									{panel.content}
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</>
	);
}
